import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Image as ImageIcon } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "../../lib/api";

export const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-[#0B1220] placeholder:text-slate-400 focus:border-[#5698C7] focus:outline-none focus:ring-1 focus:ring-[#5698C7] transition-colors duration-200 shadow-sm";
export const btnCls = "inline-flex items-center gap-2 bg-[#5698C7] text-white text-xs font-semibold tracking-wide rounded-md px-4 py-2 hover:bg-[#4587B8] transition-colors duration-200 disabled:opacity-50 shadow-sm";

export function MediaPicker({ onSelect, onClose }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex items-center justify-center p-4" data-testid="media-picker">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl max-h-[80vh] overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-sm">Biblioteca Multimedia</h3>
          <button onClick={onClose} data-testid="media-picker-close"><X size={16} className="text-slate-500" /></button>
        </div>
        {items.length === 0 && <p className="text-xs text-slate-500">Biblioteca vacía. Sube archivos en MATRIZ → Multimedia.</p>}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {items.map((m) => (
            <button key={m._id} onClick={() => { onSelect(m.url, m); onClose(); }} className="hud-border p-1 bg-white" data-testid={`media-pick-${m._id}`}>
              {m.content_type?.startsWith("image") ? (
                <img src={mediaUrl(m.url)} alt={m.filename} className="w-full h-20 object-cover" />
              ) : (
                <div className="h-20 flex items-center justify-center text-[10px] text-slate-500 break-all p-1">{m.filename}</div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function useConfig() {
  const [config, setConfig] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({})); }, []);
  return config;
}

function ListField({ field, value, onChange }) {
  const [text, setText] = useState(Array.isArray(value) ? value.join(", ") : value || "");
  return (
    <input className={inputCls} value={text} placeholder="Valores separados por comas"
      onChange={(e) => { setText(e.target.value); onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean)); }}
      data-testid={`field-${field.key}`} />
  );
}

function Field({ field, value, onChange }) {
  const [picker, setPicker] = useState(false);
  const v = value ?? (field.type === "i18n" || field.type === "i18n-textarea" ? { es: "", en: "" } : field.type === "switch" ? true : field.type === "number" ? 0 : "");
  const label = <label className="font-mono-tech text-[10px] tracking-widest text-slate-500 block mb-1">{field.label.toUpperCase()}</label>;
  if (field.type === "i18n" || field.type === "i18n-textarea") {
    const Comp = field.type === "i18n" ? "input" : "textarea";
    return (
      <div>{label}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {["es", "en"].map((l) => (
            <Comp key={l} rows={field.rows || 3} placeholder={l.toUpperCase()} className={inputCls} value={v[l] || ""}
              onChange={(e) => onChange({ ...v, [l]: e.target.value })} data-testid={`field-${field.key}-${l}`} />
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "switch") {
    return (
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onChange(!v)} data-testid={`field-${field.key}`}
          className={`w-10 h-5 border transition-colors duration-200 relative ${v ? "border-[#5698C7] bg-[#5698C7]/20" : "border-slate-300 bg-white"}`}>
          <span className={`absolute top-0.5 w-3.5 h-3.5 transition-transform duration-200 ${v ? "bg-[#5698C7] translate-x-5" : "bg-slate-400 translate-x-0.5"}`} style={{ left: 0 }} />
        </button>
        <span className="font-mono-tech text-[10px] tracking-widest text-slate-500">{field.label.toUpperCase()}</span>
      </div>
    );
  }
  if (field.type === "list") {
    return <div>{label}<ListField field={field} value={value} onChange={onChange} /></div>;
  }
  if (field.type === "select") {
    return (
      <div>{label}
        <select className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`}>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "media") {
    return (
      <div>{label}
        <div className="flex gap-2">
          <input className={inputCls} value={v} onChange={(e) => onChange(e.target.value)} placeholder="URL o selecciona de la biblioteca" data-testid={`field-${field.key}`} />
          <button type="button" onClick={() => setPicker(true)} className="border border-slate-300 rounded-md px-3 text-slate-500 hover:border-[#5698C7] hover:text-[#5698C7] transition-colors duration-200" data-testid={`field-${field.key}-picker`}>
            <ImageIcon size={14} />
          </button>
        </div>
        {picker && <MediaPicker onSelect={onChange} onClose={() => setPicker(false)} />}
      </div>
    );
  }
  return (
    <div>{label}
      <input type={field.type === "number" ? "number" : "text"} className={inputCls} value={v}
        onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`field-${field.key}`} />
    </div>
  );
}

export function CrudManager({ coll, title, fields, columns, testid }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get(`/admin/content/${coll}`).then((r) => setItems(r.data)).catch(() => {}); }, [coll]);
  useEffect(() => { load(); }, [load]);

  const blank = () => {
    const d = {};
    fields.forEach((f) => { d[f.key] = f.type === "i18n" || f.type === "i18n-textarea" ? { es: "", en: "" } : f.type === "switch" ? true : f.type === "number" ? 0 : f.type === "select" ? f.options[0] : f.type === "list" ? [] : ""; });
    return d;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { _id, created_at, updated_at, ...data } = editing;
      if (_id) await api.put(`/admin/content/${coll}/${_id}`, { data });
      else await api.post(`/admin/content/${coll}`, { data });
      toast.success("Guardado");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/admin/content/${coll}/${id}`);
    toast.success("Eliminado"); load();
  };

  const cellValue = (item, col) => {
    const v = item[col.key];
    if (v == null) return "—";
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object") return v.es || v.en || "—";
    if (typeof v === "boolean") return v ? "SÍ" : "NO";
    return String(v);
  };

  return (
    <div data-testid={testid || `${coll}-admin`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-xl font-light">{title}</h1>
        <button onClick={() => setEditing(blank())} className={btnCls} data-testid={`${coll}-add-btn`}><Plus size={14} /> AÑADIR</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => <th key={c.key} className="font-mono-tech text-[10px] tracking-widest text-slate-500 px-4 py-3">{c.label.toUpperCase()}</th>)}
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-slate-400">Sin elementos.</td></tr>}
            {items.map((item) => (
              <tr key={item._id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`${coll}-row-${item._id}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-600 max-w-[220px] truncate">{cellValue(item, c)}</td>)}
                <td className="px-4 py-3">
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setEditing(item)} className="text-slate-500 hover:text-[#5698C7] transition-colors duration-200" data-testid={`${coll}-edit-${item._id}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(item._id)} className="text-slate-500 hover:text-red-600 transition-colors duration-200" data-testid={`${coll}-delete-${item._id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-auto p-6" data-testid={`${coll}-form-modal`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-sm">{editing._id ? "Editar" : "Nuevo"} — {title}</h3>
              <button onClick={() => setEditing(null)} data-testid={`${coll}-form-close`}><X size={16} className="text-slate-500" /></button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <Field key={f.key} field={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={save} disabled={saving} className={btnCls} data-testid={`${coll}-save-btn`}>{saving ? "..." : "GUARDAR"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
